import { approvalChallengeFor, KEY_DIRECTORY_DOMAIN, } from './approval-batch.js';
import { copy, fromBase64Url, toBase64Url, utf8 } from './bytes.js';
import { exactKeys, hkdf256, positiveInteger, validId } from './content.js';
import { domainSeparated, hashCanonical, hashCanonicalB64 } from './hash.js';
import { canonicalize } from './jcs.js';
import { jwkThumbprint, signP256, verifyP256 } from './keys.js';
import { importAgreementPublicKey, wrapAccountKey } from './wrapping.js';
export const SELF_JOIN_PROOF_DOMAIN = 'viko/directory-self-join/v1';
/**
 * Proof of possession of the current epoch's ADK for a self-join: HMAC-SHA256 keyed with
 * HKDF(ADK, info = domain + account + epoch) over the canonical (previous digest, next digest,
 * signer). Binding both digests stops replay onto another head or epoch.
 */
export async function selfJoinProof(adk, input) {
    if (adk.length !== 32)
        throw new Error('ADK must be 256 bits');
    const raw = await hkdf256(adk, new Uint8Array(0), utf8(canonicalize({
        domain: SELF_JOIN_PROOF_DOMAIN,
        accountId: input.accountId,
        epoch: input.epoch,
    })));
    try {
        const key = await globalThis.crypto.subtle.importKey('raw', copy(raw), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
        const mac = await globalThis.crypto.subtle.sign('HMAC', key, copy(domainSeparated(SELF_JOIN_PROOF_DOMAIN, { v: 1, ...input })));
        return toBase64Url(new Uint8Array(mac));
    }
    finally {
        raw.fill(0);
    }
}
/** Digest of a snapshot statement, as checkpoints record it. */
export function directoryDigest(statement) {
    return hashCanonicalB64('viko/key-directory/v1', statement);
}
/** A trusted browser's self-join, signed by its device key and carrying the ADK proof. */
export async function signSelfJoin(statement, previousDigest, device, adk) {
    const signed = await signKeyDirectory(statement, device);
    const proof = await selfJoinProof(adk, {
        accountId: statement.accountId,
        epoch: statement.epoch,
        previous: previousDigest,
        next: await directoryDigest(statement),
        signer: device.deviceKeyId,
    });
    return { ...signed, approval: { ...signed.approval, proof } };
}
/** Public commitment to a high-entropy ADK, authenticated by the directory signature. */
export async function accountKeyCommitment(adk, accountId, epoch) {
    validId(accountId);
    positiveInteger(epoch);
    if (adk.length !== 32)
        throw new Error('ADK must be 256 bits');
    return hashCanonicalB64('viko/adk-commitment/v1', { accountId, epoch, adk: toBase64Url(adk) });
}
export function createDirectoryChallenge(statement) {
    return hashCanonical(KEY_DIRECTORY_DOMAIN, statement);
}
export async function signKeyDirectory(statement, device) {
    return {
        statement: structuredClone(statement),
        approval: {
            type: 'device',
            deviceKeyId: device.deviceKeyId,
            signature: toBase64Url(await signP256(device.privateKey, domainSeparated('viko/key-directory/v1', statement))),
        },
    };
}
/** Local trusted checkpoint, never seeded from an unauthenticated server response. */
export class KeyDirectoryVerifier {
    accountId;
    trust;
    #checkpoint;
    #busy = false;
    #options;
    constructor(accountId, trust, checkpoint, options = {}) {
        this.accountId = accountId;
        this.trust = trust;
        this.#options = options;
        validId(accountId);
        if (checkpoint &&
            (checkpoint.statement.accountId !== accountId ||
                checkpoint.revision !== checkpoint.statement.revision))
            throw new Error('invalid directory checkpoint');
        this.#checkpoint = checkpoint ? structuredClone(checkpoint) : undefined;
    }
    get checkpoint() {
        return this.#checkpoint ? structuredClone(this.#checkpoint) : undefined;
    }
    recipient(id) {
        const r = this.#checkpoint?.statement.recipients.find((r) => r.id === id);
        if (!r)
            throw new Error('unapproved wrapping recipient');
        if (this.#checkpoint?.unproven?.includes(id))
            throw new Error('unproven wrapping recipient');
        if ((r.type === 'device' || r.type === 'daemon') &&
            (this.trust.isDeviceRevoked(id) ||
                (r.type === 'device' && this.trust.lookupDevice(id).status === 'revoked')))
            throw new Error('revoked wrapping recipient');
        if (r.type === 'prf' && this.trust.isCredentialRevoked(id))
            throw new Error('revoked wrapping recipient');
        return structuredClone(r);
    }
    /** Safe wrapping entry point: uses the verified directory, never a caller-supplied key. */
    async wrapFor(adk, recipientId) {
        const r = this.recipient(recipientId);
        const c = this.#checkpoint;
        if (!c || (r.type !== 'device' && r.type !== 'daemon'))
            throw new Error('recipient is not ECDH');
        if (c.statement.revocationSeq !== this.trust.revocationSeq)
            throw new Error('directory requires revocation sync');
        if ((await accountKeyCommitment(adk, this.accountId, c.statement.epoch)) !==
            c.statement.keyCommitment)
            throw new Error('ADK commitment mismatch');
        return wrapAccountKey(adk, { version: 1, accountId: this.accountId, epoch: c.statement.epoch, recipientId }, r.publicKey);
    }
    async accept(input, now = Date.now()) {
        const [checkpoint] = await this.acceptChain([input], now);
        return checkpoint;
    }
    /**
     * Offline catch-up across any number of revocation transitions. Every snapshot must link to its
     * predecessor (revision + 1, previous digest, epoch/revocation monotonicity, rotation on removal).
     * The final snapshot is verified under the CURRENT trust state (exact revocation seq, live
     * signer). Superseded snapshots were validated by their signers' endpoints at the time; they are
     * checked against revocations known as of their own seq, with signatures verified whenever the
     * signer is still verifiable, and are otherwise authenticated through the digest chain that the
     * currently trusted final signature covers. Nothing is installed unless the whole chain passes.
     */
    /**
     * Wrap to a PRF/recovery escrow recipient. The directory signs only its key id, so the relay's
     * public key is accepted only when its RFC 7638 thumbprint equals that signed key id.
     */
    async wrapForEscrow(adk, recipientId, publicKey) {
        const r = this.recipient(recipientId);
        const c = this.#checkpoint;
        if (!c || (r.type !== 'prf' && r.type !== 'recovery'))
            throw new Error('recipient is not escrow');
        await importAgreementPublicKey(publicKey);
        if ((await jwkThumbprint(publicKey)) !== r.keyId)
            throw new Error('escrow key mismatch');
        if (c.statement.revocationSeq !== this.trust.revocationSeq)
            throw new Error('directory requires revocation sync');
        if ((await accountKeyCommitment(adk, this.accountId, c.statement.epoch)) !==
            c.statement.keyCommitment)
            throw new Error('ADK commitment mismatch');
        return wrapAccountKey(adk, { version: 1, accountId: this.accountId, epoch: c.statement.epoch, recipientId }, publicKey);
    }
    async acceptChain(inputs, now = Date.now()) {
        if (this.#busy)
            throw new Error('serialize directory updates');
        this.#busy = true;
        try {
            if (!Array.isArray(inputs) || !inputs.length || inputs.length > 256)
                throw new Error('invalid directory chain');
            // Snapshot before the first await: untrusted callers cannot mutate what gets installed.
            const chain = structuredClone(inputs);
            let p = this.#checkpoint;
            const out = [];
            for (let i = 0; i < chain.length; i++) {
                p = await this.check(chain[i], p, now, i === chain.length - 1);
                out.push(p);
            }
            this.#checkpoint = p;
            return structuredClone(out);
        }
        finally {
            this.#busy = false;
        }
    }
    async check(signed, p, now, head) {
        exactKeys(signed, ['statement', 'approval']);
        const s = signed.statement;
        await this.validate(s, now, head);
        if (!p) {
            if (s.revision !== 1 ||
                s.previous !== null ||
                s.epoch !== 1 ||
                signed.approval.type !== 'passkey')
                throw new Error('directory genesis requires pinned passkey');
        }
        else {
            if (s.revision !== p.revision + 1 ||
                s.previous !== p.digest ||
                s.epoch < p.statement.epoch ||
                s.epoch > p.statement.epoch + 1 ||
                s.revocationSeq < p.statement.revocationSeq)
                throw new Error('directory rollback or fork');
            if (s.epoch === p.statement.epoch && s.keyCommitment !== p.statement.keyCommitment)
                throw new Error('ADK changed without rotation');
            const removed = p.statement.recipients.some((old) => !s.recipients.some((r) => canonicalize(r) === canonicalize(old)));
            if ((removed || s.revocationSeq > p.statement.revocationSeq) &&
                s.epoch !== p.statement.epoch + 1)
                throw new Error('recipient revocation requires new epoch');
        }
        const approval = signed.approval;
        let selfJoin = false;
        if (approval.type === 'passkey') {
            exactKeys(approval, approval.batch === undefined ? ['type', 'assertion'] : ['type', 'assertion', 'batch']);
            const challenge = await approvalChallengeFor(KEY_DIRECTORY_DOMAIN, await createDirectoryChallenge(s), approval.batch);
            const result = head
                ? await this.trust.verifyPasskeyApproval(approval.assertion, challenge)
                : await this.trust.verifyHistoricalPasskeyApproval(approval.assertion, challenge, s.revocationSeq);
            if (!result.ok)
                throw new Error('unapproved directory signer');
        }
        else if (approval.type === 'device') {
            exactKeys(approval, approval.proof === undefined
                ? ['type', 'deviceKeyId', 'signature']
                : ['type', 'deviceKeyId', 'signature', 'proof']);
            // Only a device in the previous directory may approve the next snapshot, except a trusted
            // browser's self-join: it appends exactly itself and changes nothing else (docs/e2e.md
            // "Trusted browsers follow rotations"). Its endorsement is checked below like any signer.
            // An unproven self-joined recipient approves nothing.
            const established = !!p?.statement.recipients.some((r) => r.type === 'device' && r.id === approval.deviceKeyId) && !p.unproven?.includes(approval.deviceKeyId);
            selfJoin = !established && !!p && isSelfJoin(p.statement, s, approval.deviceKeyId);
            if (!established && !selfJoin)
                throw new Error('signer not in pinned directory');
            if (typeof approval.signature !== 'string' || fromBase64Url(approval.signature).length !== 64)
                throw new Error('invalid directory signature');
            const lookup = this.trust.lookupDevice(approval.deviceKeyId, head ? now : s.issuedAt);
            // A "Just this session" device never approves directory changes.
            if (lookup.status === 'ok' && lookup.device.session)
                throw new Error('session device cannot approve directory');
            if (lookup.status === 'ok') {
                const key = lookup.device.publicKey;
                if (!(await verifyP256(key, fromBase64Url(approval.signature), domainSeparated('viko/key-directory/v1', s))))
                    throw new Error('invalid directory signature');
            }
            else if (head || this.trust.revokedAsOf(approval.deviceKeyId, s.revocationSeq))
                throw new Error('unapproved directory signer');
            // else: a since-retired signer; authenticated by the later trusted snapshot's digest chain.
        }
        else
            throw new Error('unsupported directory approval');
        const digest = await hashCanonicalB64('viko/key-directory/v1', s);
        // Self-join proofs (docs/e2e.md): quarantine what this endpoint cannot prove.
        let unproven = (p?.unproven ?? []).filter((id) => s.recipients.some((r) => r.id === id));
        if (selfJoin) {
            if (!this.#options.relay && !(await this.proven(signed, p, digest)))
                unproven = [...unproven, approval.deviceKeyId];
        }
        else if (p && s.epoch === p.statement.epoch + 1)
            unproven = []; // an approved rotation vouches
        return { revision: s.revision, digest, statement: s, ...(unproven.length ? { unproven } : {}) };
    }
    /** Check a self-join's ADK proof with this endpoint's key for that epoch, if it holds one. */
    async proven(signed, p, digest) {
        const approval = signed.approval;
        if (approval.type !== 'device' || typeof approval.proof !== 'string')
            return false;
        const s = signed.statement;
        const raw = await this.#options.epochKey?.(s.epoch, s.keyCommitment).catch(() => undefined);
        if (!raw)
            return false;
        try {
            if (raw.length !== 32 ||
                (await accountKeyCommitment(raw, this.accountId, s.epoch)) !== s.keyCommitment)
                return false;
            const expected = await selfJoinProof(raw, {
                accountId: this.accountId,
                epoch: s.epoch,
                previous: p.digest,
                next: digest,
                signer: approval.deviceKeyId,
            });
            return constantTimeEqual(expected, approval.proof);
        }
        finally {
            raw.fill(0);
        }
    }
    async validate(s, now, head = true) {
        exactKeys(s, [
            'version',
            'accountId',
            'revision',
            'previous',
            'epoch',
            'keyCommitment',
            'revocationSeq',
            'issuedAt',
            'recipients',
        ]);
        if (s.version !== 1 || s.accountId !== this.accountId)
            throw new Error('wrong directory account/version');
        positiveInteger(s.revision);
        positiveInteger(s.epoch);
        if (typeof s.keyCommitment !== 'string' || fromBase64Url(s.keyCommitment).length !== 32)
            throw new Error('invalid ADK commitment');
        if (s.previous !== null &&
            (typeof s.previous !== 'string' || fromBase64Url(s.previous).length !== 32))
            throw new Error('invalid directory previous digest');
        if (!Number.isSafeInteger(s.revocationSeq) ||
            (head
                ? s.revocationSeq !== this.trust.revocationSeq
                : s.revocationSeq > this.trust.revocationSeq))
            throw new Error('directory revocation state mismatch');
        if (!Number.isSafeInteger(s.issuedAt) || s.issuedAt < 0 || s.issuedAt > now + 60_000)
            throw new Error('invalid directory time');
        if (!Array.isArray(s.recipients) || s.recipients.length < 1 || s.recipients.length > 1024)
            throw new Error('invalid directory recipients');
        const ids = new Set();
        for (const r of s.recipients) {
            validId(r.id);
            if (ids.has(r.id))
                throw new Error('duplicate recipient');
            ids.add(r.id);
            if (r.type === 'device' || r.type === 'daemon') {
                exactKeys(r, ['id', 'type', 'publicKey']);
                if (head
                    ? this.trust.isDeviceRevoked(r.id) ||
                        (r.type === 'device' && this.trust.lookupDevice(r.id, now).status === 'revoked')
                    : this.trust.revokedAsOf(r.id, s.revocationSeq))
                    throw new Error('revoked recipient');
                // A "Just this session" device is never a wrapping recipient: the wrap would outlive it.
                if (r.type === 'device' && head) {
                    const known = this.trust.lookupDevice(r.id, now);
                    if ((known.status === 'ok' || known.status === 'expired') && known.device.session)
                        throw new Error('session device cannot be a recipient');
                }
                await importAgreementPublicKey(r.publicKey);
            }
            else if (r.type === 'prf' || r.type === 'recovery') {
                exactKeys(r, ['id', 'type', 'keyId']);
                validId(r.keyId);
                if (r.type === 'prf' &&
                    (head
                        ? this.trust.isCredentialRevoked(r.id)
                        : this.trust.revokedAsOf(r.id, s.revocationSeq)))
                    throw new Error('revoked recipient');
            }
            else
                throw new Error('invalid recipient type');
        }
    }
}
/**
 * A trusted browser's self-join: the same epoch, ADK commitment and revocation state, every
 * previous recipient unchanged, plus exactly one new device recipient whose id is the signer's.
 * The signer must still hold a live, non-session endorsement (checked by the caller), so only a
 * device the user's passkey trusted can receive later epochs this way; a revocation drops it.
 */
export function isSelfJoin(previous, next, signer) {
    if (next.epoch !== previous.epoch ||
        next.keyCommitment !== previous.keyCommitment ||
        next.revocationSeq !== previous.revocationSeq ||
        next.recipients.length !== previous.recipients.length + 1 ||
        previous.recipients.some((r) => r.id === signer))
        return false;
    const old = new Set(previous.recipients.map((r) => canonicalize(r)));
    const added = next.recipients.filter((r) => !old.has(canonicalize(r)));
    return added.length === 1 && added[0].type === 'device' && added[0].id === signer;
}
function constantTimeEqual(a, b) {
    if (a.length !== b.length)
        return false;
    let d = 0;
    for (let i = 0; i < a.length; i++)
        d |= a.charCodeAt(i) ^ b.charCodeAt(i);
    return d === 0;
}
//# sourceMappingURL=key-directory.js.map